import React from 'react';
import clsx from 'clsx';
import {
  Row,
} from '@tanstack/react-table';
import {
  NPISearchItemDTO,
} from 'dtos';
import {
  useSelectableContext,
} from 'core/hooks';
import Table from 'core/components/VirtualTable';
import {
  organizationColumns,
  individualColumns,
} from 'pages/Dashboard/components/ReferringProvider/columns';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useFormContext,
} from 'react-hook-form';
import {
  ProviderSearchParams,
} from 'pages/Dashboard/types/provider';

type Props = {
  data: NPISearchItemDTO[];
  queryParams: ProviderSearchParams | null;
  isSearching: boolean;
  setSelectedRow: (provider: NPISearchItemDTO | null) => void;
  isFetched: boolean;
};

export default function SearchResult({
  isSearching,
  data,
  setSelectedRow,
  isFetched,
}: Props) {
  const { isSelected, toggle } = useSelectableContext();
  const isTransitionalState = isSearching || !isFetched || !data?.length;
  const { watch } = useFormContext();
  const npiType = watch('npiType');
  const isOrganization = npiType === 'NPI-2 Organization';
  const columns = isOrganization ? organizationColumns : individualColumns;

  return (
    <div className={clsx('flex flex-1 flex-col overflow-hidden', {
      'items-center justify-center': isTransitionalState,
    })}
    >
      {isSearching ? (
        <div className="flex items-center gap-2">
          <Spinner size={24} />
          Searching...
        </div>
      ) : (
        <div className="flex flex-1 w-full h-full overflow-y-hidden flex-col">
          <Table<NPISearchItemDTO>
            data={data ?? []}
            getRowId={(row) => row.npi}
            columns={columns}
            className="!pb-0"
            classes={{
              scrollContainer: 'justify-center items-center !overflow-hidden',
              header: 'px-4',
              outerRow: '!w-[calc(100%-16px)] left-4',
              row: (row: Row<NPISearchItemDTO>) => clsx('!items-start', {
                'bg-selected-entity text-black': isSelected(row.id),
              }),
            }}
            onRowClick={(_table, row) => {
              const nextSelected = isSelected(row.id) ? null : row.original;
              toggle(row.id);
              setSelectedRow(nextSelected);
            }}
            emptySlot={!isFetched ? (
              <div className="text-center">
                Enter
                {' '}
                {isOrganization
                  ? <strong className="font-semibold">Organization Name</strong>
                  : (
                    <>
                      Provider&apos;s
                      {' '}
                      <strong className="font-semibold">First</strong>
                      {' and/or '}
                      <strong className="font-semibold">Last Names</strong>
                    </>
                  )}
                {' '}
                and
                {' '}
                <strong className="font-semibold">State</strong>
                ,
                <br />
                or
                {' '}
                <strong className="font-semibold">NPI (10 digits)</strong>
                {' '}
                {' '}
                to get results.
              </div>
            ) : 'No providers were found. Try to adjust searching criteria to get results.'}
          />
        </div>
      )}
    </div>
  );
}
